// What auto-signup will do, in one sentence. Mirrors describeSignup in
// volocal/src/signup_prefs.ts (which renders it for the Discord panel); keep
// the two in step.

import { ReactNode } from "react";

const DAY_SHORT = ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const LEVEL_LABELS: Record<string, string> = {
  beginner: "Beginner/Social",
  intermediate: "Intermediate",
  advanced: "Advanced/Competitive",
};

const andList = (items: string[]) =>
  items.length <= 1
    ? items.join("")
    : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

// Runs of four or more days become a range; shorter runs are spelled out.
export function describeDays(days: number[]): string {
  const sorted = Array.from(new Set(days)).sort((a, b) => a - b);
  if (sorted.length === 2 && sorted[0] === 6 && sorted[1] === 7) {
    return "weekends";
  }
  const runs: number[][] = [];
  for (const d of sorted) {
    const run = runs[runs.length - 1];
    if (run && d === run[run.length - 1] + 1) run.push(d);
    else runs.push([d]);
  }
  return andList(
    runs.flatMap((run) =>
      run.length >= 4
        ? [`${DAY_SHORT[run[0]]}–${DAY_SHORT[run[run.length - 1]]}`]
        : run.map((d) => DAY_SHORT[d])
    )
  );
}

const time = (m: number) => {
  const h = Math.floor(m / 60) % 12 || 12;
  const mm = m % 60;
  return `${h}${mm ? `:${String(mm).padStart(2, "0")}` : ""}${m < 12 * 60 ? "am" : "pm"}`;
};

// Joins bolded items as "A, B and C".
const boldList = (items: string[]): ReactNode[] =>
  items.flatMap((item, i) => [
    i === 0 ? "" : i === items.length - 1 ? " and " : ", ",
    <b key={item}>{item}</b>,
  ]);

export function SignupSummary({
  days,
  neighborhoods,
  levels,
  earliest,
  latest,
}: {
  days: number[] | null;
  neighborhoods: string[] | null;
  levels: string[] | null;
  earliest: number | null;
  latest: number | null;
}) {
  const when =
    earliest !== null && latest !== null
      ? `between ${time(earliest)} and ${time(latest)}`
      : earliest !== null
      ? `${time(earliest)} or later`
      : latest !== null
      ? `by ${time(latest)}`
      : null;
  return (
    <>
      You'll be signed up for{" "}
      {levels?.length ? (
        <>{boldList(levels.map((l) => LEVEL_LABELS[l] ?? l))} pickups</>
      ) : (
        <b>every pickup</b>
      )}
      {neighborhoods?.length ? <> in {boldList(neighborhoods)}</> : null}
      {days?.length && days.length < 7 ? (
        <>
          {" "}
          on <b>{describeDays(days)}</b>
        </>
      ) : null}
      {when ? (
        <>
          , starting <b>{when}</b>
        </>
      ) : null}
      .
    </>
  );
}
